import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  Button,
  ButtonVariant,
  FileUploader,
  FormCheckbox,
  FormGroup,
  FormInput,
  FormTextarea,
  LoadingCircle,
  TextButton,
  useToast,
} from "@industriousoffice/react-components";
import { usesACH } from "@/utils/location";

import { opbApi } from "@/api/opb/opbApi";
import useAccount from "@/hooks/useAccount";
import { SplittedPage } from "@/components/SplittedPage/SplittedPage";
import { FAQ_LINK } from "@/constants";
import { PageTitle } from "@/components/PageTitle";
import { SecurityDepositRefundStatus } from "@/api/opb/opbTypes";

import {
  AchSecurityDepositSchema,
  AchSecurityDepositSchemaType,
} from "./schemas/SecurityDepositSchema";
import { Success } from "./components/Success";

const defaultValues: AchSecurityDepositSchemaType = {
  transferType: "ach",
  accountHolderName: "",
  bankInstitutionName: "",
  bankRoutingNumber: "",
  bankAccountNumber: "",
  termsAndConditions: false,
  file: null as any, // expected by yup resolver to be a File, but empty at first
  bankSWIFTCode: "",
  bankSortCode: "",
  bankCountry: "",
};

export function CreateSecurityDeposit() {
  const { t } = useTranslation("securityDeposit");
  const { accountId } = useParams();
  const { component, toast } = useToast();
  const [isSuccessful, setIsSuccessful] = useState(false);
  const navigate = useNavigate();

  const { name, location, account, isLoadingAccount } = useAccount(accountId, {
    withInvoices: true,
    withBalance: true,
    withPaymentMethods: true,
  });

  const useTransferType: AchSecurityDepositSchemaType["transferType"] =
    location && usesACH(location) ? "ach" : "swift";

  const {
    control,
    formState: { isDirty, isValid, isSubmitting, errors },
    handleSubmit,
    setValue,
    watch,
    trigger,
    clearErrors,
  } = useForm({
    mode: "onChange",
    defaultValues,
    values: {
      ...defaultValues,
      transferType: useTransferType,
    },
    resolver: yupResolver<AchSecurityDepositSchemaType>(
      AchSecurityDepositSchema as any
    ),
  });

  const file = watch("file");
  const transferType = watch("transferType");

  const onSubmit = handleSubmit(async (data: AchSecurityDepositSchemaType) => {
    const formData = new FormData();

    if (data.transferType === "ach") {
      formData.append("bankABACode", data.bankRoutingNumber!); // field was validated by yup, we know it's not undefined, hence the !
    } else if (data.transferType === "swift") {
      formData.append("bankSWIFTCode", data.bankSWIFTCode!);
      formData.append("bankSortCode", data.bankSortCode!);
      formData.append("bankCountry", data.bankCountry!);
      formData.append("bankAddress", data.bankAddress!);
    }
    formData.append("type", data.transferType);
    formData.append("bankName", data.bankInstitutionName);
    formData.append("bankAccountNumber", data.bankAccountNumber);
    formData.append("bankAccountHolderName", data.accountHolderName);
    formData.append("file", data.file);
    formData.append("fileName", data.file.name);

    try {
      await opbApi.updateSecurityDeposit(accountId!, formData);
      setIsSuccessful(true);
    } catch (e) {
      Sentry.captureException(e);
      // eslint-disable-next-line no-console
      console.error(e);
      toast(
        t(
          "An error occurred while submitting the form. Please try again later."
        ),
        true
      );
      setIsSuccessful(false);
    }
  });

  if (isLoadingAccount) {
    return (
      <div className="flex justify-center m-12">
        <LoadingCircle spinnerSize={32} />
      </div>
    );
  }
  if (!account) {
    return <Navigate to="/" />;
  }

  if (
    account?.securityDeposit?.refundStatus &&
    account?.securityDeposit?.refundStatus !==
      SecurityDepositRefundStatus.Open &&
    account?.securityDeposit?.refundStatus !==
      SecurityDepositRefundStatus.IncorrectBankInfo
  ) {
    navigate("/");
    return null;
  }

  return (
    <form onSubmit={onSubmit}>
      {component}
      <SplittedPage>
        <SplittedPage.Left className="pb-8 h-full">
          {isSuccessful && <Success />}
          {!isSuccessful && (
            <>
              <PageTitle>{t("Billing | Security Deposit Form")}</PageTitle>
              <div className="text-xl text-bold pt-2">{name}</div>
              <p className="text-grey-40 py-8">
                {t(
                  "This form should only be used as part of your security deposit refund process. In order to process the refund for your security deposit, please complete the form below."
                )}
              </p>
              <h3 className="pb-4 font-bold">
                {t("Bank Details for Security Deposit Refund at")}
                {` ${location?.name || ""}`}
              </h3>
              <div>
                <FormGroup>
                  <FormInput
                    control={control}
                    name="accountHolderName"
                    rules={{ required: true }}
                    label={t(
                      "Name of Account Holder (as it appears on the bank statement/account)"
                    )}
                    isRequired
                    data-testid="security_deposit-holder_name-input"
                  />
                </FormGroup>
                {transferType === "swift" && (
                  <FormGroup>
                    <FormInput
                      control={control}
                      name="bankSWIFTCode"
                      rules={{ required: true }}
                      label={t("Bank SWIFT Code")}
                      isRequired
                      data-testid="security_deposit-swift-input"
                    />
                  </FormGroup>
                )}
                {transferType === "swift" && (
                  <FormGroup>
                    <FormInput
                      control={control}
                      name="bankSortCode"
                      rules={{ required: true }}
                      label={t("Bank/Sort Code")}
                      isRequired
                      data-testid="security_deposit-sort_code-input"
                    />
                  </FormGroup>
                )}
                {transferType === "ach" && (
                  <FormGroup>
                    <FormInput
                      control={control}
                      name="bankRoutingNumber"
                      rules={{ required: true }}
                      label={t("ACH Routing Number")}
                      isRequired
                      data-testid="security_deposit-bank_routing-input"
                    />
                  </FormGroup>
                )}
                <FormGroup>
                  <FormInput
                    control={control}
                    name="bankAccountNumber"
                    label={
                      // eslint-disable-next-line no-nested-ternary
                      transferType === "ach"
                        ? t("Bank Account Number")
                        : t(
                            "Bank Account Number (or 34 character IBAN for Euro)"
                          )
                    }
                    isRequired
                    data-testid="security_deposit-account_number-input"
                  />
                </FormGroup>
                <FormGroup>
                  <FormInput
                    control={control}
                    name="bankInstitutionName"
                    rules={{ required: true }}
                    label={t("Bank Institution Name")}
                    isRequired
                    data-testid="security_deposit-bank_name-input"
                  />
                </FormGroup>
                {transferType === "swift" && (
                  <FormGroup>
                    <FormInput
                      control={control}
                      name="bankCountry"
                      rules={{ required: true }}
                      label={t("Bank Country")}
                      isRequired
                      data-testid="security_deposit-bank_country-input"
                    />
                  </FormGroup>
                )}
                {transferType === "swift" && (
                  <FormGroup className="mb-6">
                    <FormTextarea
                      rows={3}
                      control={control}
                      name="bankAddress"
                      rules={{ required: true }}
                      label={t("Bank Address")}
                      isRequired
                      data-testid="security_deposit-bank_address-input"
                    />
                  </FormGroup>
                )}
              </div>
              <h3 className="pb-2 font-bold">
                {t("Bank Verification Document")}
              </h3>
              <p className="text-grey-40">
                {t(
                  "Please upload a bank letter or voided check to verify your bank details."
                )}
              </p>
              <FileUploader
                isLoading={isSubmitting}
                file={file as File}
                onFileChange={(f) => {
                  setValue("file", f as File);
                  trigger("file");
                }}
                className="mt-4 h-80"
                isError={!!errors.file}
                labels={{
                  defaultTitle: t("Drag & Drop"),
                  defaultDescription: t(
                    "Your upload is secure and fully encrypted"
                  ),
                  errorTitle: errors.file?.message ? t("Invalid File") : "",
                  errorDescription: errors.file?.message || "",
                  readyToUploadDescription: t(
                    "Your upload is secure and fully encrypted"
                  ),
                  browseFiles: t("or browse files"),
                }}
              />
              <div className="flex justify-end">
                {file && (
                  <TextButton
                    className="text-red-60 underline pr-2"
                    onClick={() => {
                      setValue("file", null as any);
                      clearErrors("file");
                    }}
                  >
                    {t("Remove file")}
                  </TextButton>
                )}
              </div>
              <FormGroup className="pt-6">
                <FormCheckbox
                  control={control}
                  name="termsAndConditions"
                  rules={{ required: true }}
                  label={t(
                    "I hereby certify that the above information is correct and true, and that I am authorized to designate payment by Industrious to the specified account. I acknowledge my security deposit will be returned to the account information provided above within 45 days of submitting this form -pending that the data provided is complete, accurate and valid- or after the move out date, whichever comes later. I agree to indemnify Industrious for any damages caused by their reliance on the information I've provided."
                  )}
                />
              </FormGroup>
              <Button
                className="font-hanken w-full sm:w-56 sm:mt-0 mt-4"
                variant={ButtonVariant.Primary}
                type="submit"
                isDisabled={!isDirty || !isValid}
                isLoading={isSubmitting}
              >
                {t("Submit")}
              </Button>
            </>
          )}
        </SplittedPage.Left>
        <SplittedPage.Right className="flex lg:items-end h-full">
          <div className="lg:mb-24">
            <h5 className="font-bold text-2xl mt-12">{t("Questions?")}</h5>
            <p>
              {t("View our ")}
              <a
                href={FAQ_LINK}
                target="_blank"
                rel="noreferrer"
                className="underline text-teal-60"
              >
                {t("FAQ page")}
              </a>
              {t(", or contact your Member Experience Manager.")}
            </p>
          </div>
        </SplittedPage.Right>
      </SplittedPage>
    </form>
  );
}
